import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { ContentItem, ContentTranslation, ModuleDetail, ModuleSummary } from "../types";

type Dictionary = Record<string, string>;

// Swaps every string that has a translation, wherever it sits in the
// payload (question text, option text, captions, file URLs, ...). IDs and
// structure are untouched, so answers still reach the server unchanged.
function replaceStrings<T>(value: T, dictionary: Dictionary): T {
  // Older saves stored the key trimmed, so fall back to the trimmed text.
  if (typeof value === "string")
    return (dictionary[value] ?? dictionary[value.trim()] ?? value) as T;
  if (Array.isArray(value)) return value.map((entry) => replaceStrings(entry, dictionary)) as T;
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [key, replaceStrings(entry, dictionary)])
    ) as T;
  }
  return value;
}

export function localizeItem(item: ContentItem, language: string): ContentItem {
  const translation: ContentTranslation | undefined = item.translations?.[language];
  if (!translation) return item;
  const strings = translation.strings ?? {};
  return {
    ...item,
    title: translation.title || item.title,
    content: Object.keys(strings).length ? replaceStrings(item.content, strings) : item.content,
  };
}

export function localizeModule(module: ModuleDetail, language: string): ModuleDetail {
  return {
    ...module,
    title: module.translations?.[language]?.title || module.title,
    items: module.items.map((item) => localizeItem(item, language)),
  };
}

export function localizedTitle(
  summary: Pick<ModuleSummary, "title" | "translations">,
  language: string
) {
  return summary.translations?.[language]?.title || summary.title;
}

// The module in the app's current language; switching language re-renders
// with the other wording without reloading anything.
export function useLocalizedModule<T extends ModuleDetail | null>(module: T): T {
  const { i18n } = useTranslation();
  return useMemo(
    () => (module ? localizeModule(module, i18n.language) : module) as T,
    [module, i18n.language]
  );
}
